import { useEffect, useState } from "react";
import { XIcon } from "@heroicons/react/outline";

const VoucherFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  loading,
  mode = "create",
  ticketTypes = [],
  initialData = null,
}) => {
  const [selectedTicketTypes, setSelectedTicketTypes] = useState([]);

  const [form, setForm] = useState({
    name: "",
    code: "",
    type: "",
    value: "",
    status: "",
  });

  useEffect(() => {
    if (mode === "edit" && initialData) {
      setForm({
        name: initialData.name || "",
        code: initialData.code || "",
        type: initialData.type || "",
        value: initialData.value || "",
        status: initialData.status || "",
      });

      if (initialData.ticket_types?.length) {
        const mapped = ticketTypes.filter((t) =>
          initialData.ticket_types.includes(t.id),
        );

        setSelectedTicketTypes(mapped);
      }
    }

    if (mode === "create") {
      setForm({
        name: "",
        code: "",
        type: "",
        value: "",
        status: "",
      });

      setSelectedTicketTypes([]);
    }
  }, [mode, initialData, ticketTypes]);

  if (!isOpen) return null;

  const handleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = () => {
    onSubmit({
      ...form,
      ticket_types: selectedTicketTypes.map((t) => t.id),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-lg bg-slate-900 border border-white/10 rounded-xl shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">
            {mode === "edit" ? "Edit Voucher" : "Add Voucher"}
          </h2>

          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-slate-300 mb-1">Name</label>
              <input
                type="text"
                placeholder="voucher name"
                value={form.name}
                onChange={(e) => handleChange("name", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-1">Code</label>
              <input
                type="text"
                placeholder="voucher code"
                value={form.code}
                onChange={(e) => handleChange("code", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <h3 className="text-sm text-slate-300 mb-1">Select Type</h3>
              <select
                value={form.type}
                onChange={(e) => handleChange("type", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              >
                <option value="">Select</option>
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed</option>
              </select>
            </div>

            <div>
              <label className="block text-sm text-slate-300 mb-1">Value</label>
              <input
                type="number"
                placeholder="Enter value"
                value={form.value}
                onChange={(e) => handleChange("value", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <h3 className="text-sm text-slate-300 mb-1">Select Status</h3>
              <select
                value={form.status}
                onChange={(e) => handleChange("status", e.target.value)}
                className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
              >
                <option value="">Select</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-1">
              Ticket Types
            </label>

            {/* Selected Tags */}
            <div className="flex flex-wrap gap-2 mb-2">
              {selectedTicketTypes.map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 bg-slate-800 border border-white/10 px-3 py-1 rounded-lg text-sm"
                >
                  {item.name}
                  <button
                    onClick={() =>
                      setSelectedTicketTypes((prev) =>
                        prev.filter((_, idx) => idx !== i),
                      )
                    }
                    className="text-slate-400 hover:text-red-400"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            {/* Dropdown */}
            <select
              value=""
              onChange={(e) => {
                const id = Number(e.target.value);
                if (!id) return;

                const selected = ticketTypes.find((t) => t.id === id);

                // prevent duplicates
                if (selectedTicketTypes.some((t) => t.id === id)) return;

                setSelectedTicketTypes((prev) => [...prev, selected]);

                e.target.selectedIndex = 0;
              }}
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white"
            >
              <option value="">Select ticket type</option>
              {ticketTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`px-4 py-2 text-sm rounded-lg text-white font-medium transition
              ${
                loading
                  ? "bg-pink-400 cursor-not-allowed"
                  : "bg-pink-600 hover:bg-pink-700"
              }
            `}
          >
            {loading
              ? "Saving..."
              : mode === "edit"
                ? "Update Voucher"
                : "Create Voucher"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VoucherFormModal;
